import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import capitalize from 'lodash.capitalize';
import uniqueId from 'lodash.uniqueid';
import {DateRangeInput, FormInput} from 'src/components/input';
import {PricingModel} from 'src/openapi/admin/models/PricingModel';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import Table, {Body, Cell, Row} from '@pb/kit-component-table';
import {LocaleContext} from '@pb/kit-context-locale';

import {getCredits, getDate} from '../../../../utils';
import {PRICING_MODEL_LABEL} from '../../../constants';
import {textAlign} from '../../../tabs-structure.module.styl';
import {CONTRACT_CELL_WIDTH as WIDTH} from '../../contracts/components/constants';
import {apiV2ContractTable} from '../../contracts/components/contract-table.module.styl';
import {INTEGRATION_PARTNERS} from '../constants';

import {DATE_NAMES} from './constants';

import type {ContractInfoDto} from '../typedef';


type Props = {
    expanded: boolean,
    data: ContractInfoDto,
    remainingCredits: number,
    startDate: string | null,
    endDate: string | null,
    contractNumber: string,
    showWarning: boolean,
    onChangeDates: (value: string, name?: string) => void,
    onChangeContractNumber: (value: string) => void,
    creditsDataAvailable: boolean,
    disabled?: boolean,
};

export const ContractTable = (props: Props) => {
    const {
        expanded,
        contractNumber,
        onChangeContractNumber,
        data,
        remainingCredits,
        startDate,
        onChangeDates,
        endDate,
        creditsDataAvailable,
        disabled,
    } = props;

    const [showWarning, setShowWarning] = useState(props.showWarning);

    const {locale} = useContext(LocaleContext);
    const {onCreateNote, onCloseNote} = usePushNotification();
    const warningNoteIdRef = useRef<string | null>(null);

    useEffect(() => {
        setShowWarning(props.showWarning);
    }, [props.showWarning]);

    const startDateView = useMemo(() => (
        expanded ? (
            <DateRangeInput
                id="contract-start-date-picker"
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                readonly={disabled}
                selectRangeType="start"
                onChange={value => onChangeDates(value, DATE_NAMES.startDate)}
            />
        ) : getDate(startDate, locale)
    ), [startDate, endDate, expanded, locale, disabled, onChangeDates]);

    const endDateView = useMemo(() => (
        expanded ? (
            <DateRangeInput
                id="contract-end-date-picker"
                readonly={disabled}
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                selectRangeType="end"
                onChange={value => onChangeDates(value, DATE_NAMES.endDate)}
            />
        ) : getDate(endDate, locale)
    ), [startDate, endDate, locale, expanded, disabled, onChangeDates]);

    const getContractNumber = useCallback(() => {
        return expanded ? (
            <FormInput
                value={contractNumber}
                onBlur={onChangeContractNumber}
                width={100}
                readOnly={disabled}
            />
        ) : contractNumber;
    }, [expanded, disabled, contractNumber, onChangeContractNumber]);

    const clearWarning = useCallback(() => {
        setShowWarning(false);
    }, []);

    const packageSize = data.pricingModel === PricingModel.SUBSCRIPTION ? data.packageSize : undefined;
    const initialPackage = data.pricingModel === PricingModel.INTEGRATION ? undefined : data.initialPackage;

    const integrationPartner = data.pricingModel === PricingModel.INTEGRATION ? data.integrationPartner : undefined;
    const showEndDate = data.pricingModel !== PricingModel.PAY_PER_CALL;

    const {pricingModel, primaryContract} = data;

    const align = 'middle' as const;
    const className = textAlign;

    useEffect(() => {
        const shouldShowWarning = expanded && showWarning;
        if (!shouldShowWarning) {
            if (warningNoteIdRef.current) {
                onCloseNote(warningNoteIdRef.current);
                warningNoteIdRef.current = null;
            }
            return;
        }

        if (warningNoteIdRef.current) {
            return;
        }

        const noteId = uniqueId('pricing-contract-number-warning-');
        warningNoteIdRef.current = noteId;
        onCreateNote({
            id: noteId,
            status: Status.WARNING,
            buttonProps: {
                iconProps: {type: Icons.CLOSE},
                onClick: () => {
                    clearWarning();
                    warningNoteIdRef.current = null;
                },
            },
            body: () => 'Please enter a unique contract number.',
        });

        return () => {
            if (warningNoteIdRef.current === noteId) {
                onCloseNote(noteId);
                warningNoteIdRef.current = null;
            }
        };
    }, [expanded, showWarning, onCreateNote, onCloseNote, clearWarning]);

    return (
        <div className={apiV2ContractTable}>
            <Table>
                <Body>
                    <Row highlighted={primaryContract ? !expanded : undefined}>
                        <Cell {...{align, className, width: WIDTH.contract}}>
                            {getContractNumber()}
                        </Cell>
                        <Cell {...{align, className, width: WIDTH.pricing}}>
                            {PRICING_MODEL_LABEL[pricingModel]}
                        </Cell>
                        {!creditsDataAvailable && integrationPartner ? (
                            <Cell {...{align, className, width: WIDTH.integration}}>
                                {INTEGRATION_PARTNERS[integrationPartner]}
                            </Cell>
                        ) : (
                            <Cell {...{align, className, width: WIDTH.size}}>
                                {packageSize ? capitalize(packageSize) : '-'}
                            </Cell>
                        )}
                        <Cell {...{className: 'text-align_center', width: WIDTH.start}}>
                            {startDateView}
                        </Cell>
                        <Cell {...{className: 'text-align_center', width: WIDTH.end}}>
                            {showEndDate ? endDateView : '-'}
                        </Cell>
                        {creditsDataAvailable ? (
                            <Cell {...{align, className: 'text-align_right', width: WIDTH.package}}>
                                {getCredits(initialPackage, locale)}
                            </Cell>
                        ) : null}
                        {creditsDataAvailable ? (
                            <Cell {...{align, className: 'text-align_right'}}>
                                {getCredits(remainingCredits, locale)}
                            </Cell>
                        ) : null}
                    </Row>
                </Body>
            </Table>
        </div>
    );
};
